/**
 * Der Rahmen einer Liste: Überschrift, Filter, Seitennavigator, Tabelle,
 * Seitennavigator.
 *
 * **Eine Liste sitzt nicht auf einer Karte.** Eine Karte ist eine Kachel neben
 * anderen Kacheln; eine Liste ist der Inhalt der Ansicht. Der Kasten um sie
 * herum kostete auf jeder Seite Breite, die den Spalten fehlt — und genau daran
 * scheiterte die Revisionsliste, die auf einem Full-HD-Schirm waagerecht
 * scrollte, obwohl daneben Platz frei stand. Das Design-System sagt dasselbe:
 * „Record sections are full-bleed …, divided by a single top rule, never boxed."
 *
 * **Der Seitennavigator steht oben und unten.** Wer am Ende einer Seite
 * angekommen ist, will weiterblättern, ohne zurückzuspringen; wer oben steht und
 * die Seite schon kennt, ebenso. Zwei Navigatoren sind zwei Wege zu demselben
 * Zustand und keine zwei Zustände — beide rufen dieselbe Funktion.
 *
 * Der Bereich („1–25 von 57") steht neben den Schaltern und nicht statt ihrer:
 * Er sagt, wie viel hinter der Liste steht, und unterscheidet drei fehlende
 * Zeilen von dreitausend.
 */

import type { ReactElement, ReactNode } from "react";

import { Button, Pagination, SectionHeader } from "../design-system";
import { formatNumber } from "../formatting";

/** Was auf dem Knopf steht — an jeder der drei Filterleisten dasselbe. */
const RESET = "Filter zurücksetzen";

/**
 * Der Zustand des Blätterns. `onTurn` bekommt die gewünschte Seite; das Begrenzen
 * auf den gültigen Bereich macht seit #41 der Baustein des Skills, der dafür
 * die Zielseite nimmt statt vier Richtungen.
 */
export interface Paging {
  page: number;
  pages: number;
  perPage: number;
  total: number;
  onTurn: (page: number) => void;
}

/**
 * Eine Liste über die volle Breite.
 *
 * **Der Kopf kommt aus `SectionHeader`** (#41). Der Baustein kann `title`, `icon`
 * und `actions` — genau das, was diese Stelle vorher selbst gezeichnet hat. Alles
 * andere bleibt hier: `filters`, `paging`, `first`, die Spaltenbreiten und der
 * leere Zustand sind Sache des Rumpfs, und einen Rumpf führt der V3-Baukasten
 * nicht als Komponente, sondern als Vorlage (`reference/templates/erp-akte/`).
 *
 * **Die Grenze zwischen `actions` und `headerRight`** ist die eine Entscheidung
 * dieser Komposition. `SectionHeader` blendet seine `actions` erst im Hover der
 * Sektion ein (`.sec-edit`, opacity 0 → 1) — richtig für „Bearbeiten / Felder /
 * Einklappen", falsch für alles, was **immer** sichtbar sein muss. Ein Zähler,
 * der erst unter dem Zeiger erscheint, ist keine Auskunft, und ein Knopf, der
 * eine sonst unerreichbare Fläche aufklappt, ist unter dem Zeiger versteckt.
 * Deshalb zwei Plätze:
 *
 * - `actions` — die Knöpfe **dieser** Sektion, die verschwinden dürfen;
 * - `headerRight` — was dasteht: Zähler, Filter, der Weg zum Aufklappen.
 *
 * Ausgeschrieben steht die Regel in
 * `docs/human/2026-08-27-werkzeugleiste-und-sektionen.md`.
 *
 * `first` heißt: Diese Liste ist die oberste der Ansicht und bekommt keine
 * Trennlinie über sich — die Linie trennt zwei Listen und begrenzt keine.
 *
 * `filters` steht **über** dem Seitennavigator: Erst wird ausgewählt, was in der
 * Liste steht, dann geblättert. Umgekehrt läse sich der Navigator wie ein Teil
 * der Filter.
 */
export function ListSection({
  title,
  glyph,
  headerRight = null,
  actions = null,
  filters = null,
  paging = null,
  first = false,
  children,
}: {
  title: string;
  glyph: string;
  /** Was immer sichtbar bleiben muss — Zähler, Filter, Aufklappen. */
  headerRight?: ReactNode;
  /** Die Knöpfe dieser Sektion, die der Hover führt — bearbeiten, einklappen. */
  actions?: ReactNode;
  filters?: ReactNode;
  paging?: Paging | null;
  first?: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <section
      // Beschriftet, damit der Abschnitt für den Vorleser ein Bereich mit Namen
      // ist und nicht ein weiterer Kasten: „Dateien dieser Revision" ist genau
      // das, was auch dasteht — die Überschrift zweimal zu schreiben wäre die
      // Gelegenheit, sie einmal zu ändern und einmal zu vergessen.
      aria-label={title}
      style={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        borderTop: first ? undefined : "1px solid var(--line-f)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--sp-5)",
          // Waagerecht auf der Zellpolsterung der Tabelle: Die Überschrift steht
          // damit über der ersten Spalte und nicht daneben.
          //
          // **Der Abstand nach unten steht an der Zeile und nicht im Kopf.** In
          // dieser Zeile stehen zwei Dinge — der Kopf und das, was immer sichtbar
          // bleibt —, und beide müssen denselben halten. `--sp-5` ist genau die
          // 10px, die der Baustein selbst als `paddingBottom` führt.
          padding: "var(--sp-6) var(--sp-5) var(--sp-5)",
        }}
      >
        <SectionHeader
          title={title}
          icon={glyph}
          actions={actions}
          style={{ flex: 1, minWidth: 0, paddingBottom: 0 }}
        />
        {headerRight}
      </header>

      {filters}

      <PageBar paging={paging} />
      {children}
      <PageBar paging={paging} />
    </section>
  );
}

/**
 * Ein Seitennavigator — oder nichts, solange es nichts zu blättern gibt.
 *
 * Bei einer einzigen Seite bleibt der Bereich stehen und die Schalter fallen
 * weg: „1–8 von 8" ist eine Auskunft, vier tote Schalter sind keine.
 */
function PageBar({ paging }: { paging: Paging | null }): ReactElement | null {
  if (paging === null || paging.total === 0) {
    return null;
  }

  const { page, pages, perPage, total, onTurn } = paging;
  const range = rangeOf(page, perPage, total);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "var(--sp-4)",
        padding: "var(--sp-4) var(--sp-5)",
      }}
    >
      {pages <= 1 ? (
        <span
          style={{
            fontSize: "var(--fs-11)",
            color: "var(--fg-6)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {range}
        </span>
      ) : (
        // Seit #41 der Baustein des Skills: **ein** `onChange` statt vier
        // Richtungsgriffe, und der Bereichstext gehört ihm — der Trenner
        // dazwischen fällt mit ihm weg, denn er trennte zwei Dinge, die jetzt
        // eines sind. Das Begrenzen auf den gültigen Bereich macht ebenfalls er.
        <Pagination page={page} pages={pages} rangeLabel={range} onChange={onTurn} />
      )}
    </div>
  );
}

/**
 * **Der eine Knopf, der eine Filterleiste auf ihre Vorbelegung zurückstellt.**
 *
 * Er steht hier und nicht dreimal in den Ansichten: Der Knopf und die Frage
 * „weicht überhaupt etwas ab?" sind dieselbe Entscheidung, und drei Kopien wären
 * dreimal die Gelegenheit, sie verschieden zu treffen.
 *
 * **Zurückgesetzt wird auf die Vorbelegung und nicht auf leer.** Sie steht als
 * `start` daneben — im Befundvorrat sind das „offen" und die Anzeigeschwelle
 * (`SITE_START`). Ein Zurücksetzen, das den Lebenszyklus auf „alle" stellte,
 * wäre kein Zurücksetzen, sondern ein anderer Filter.
 *
 * **Er erscheint nur, wenn etwas abweicht.** Ein Knopf, der nichts tun kann, ist
 * kein Knopf — dieselbe Entscheidung wie beim Seitennavigator darüber, der bei
 * einer einzigen Seite den Bereich stehen lässt und die Schalter weglässt.
 *
 * **Die Seite gehört nicht zum Vergleich.** Wer nur geblättert und keinen Filter
 * gesetzt hat, sieht den Knopf nicht: Er heißt „Filter zurücksetzen", und für
 * die Seite gibt es den Navigator. Zurückgestellt wird sie trotzdem mit, weil
 * das jede Filteränderung ohnehin tut. Deshalb bekommt diese Stelle den
 * Ausschnitt **ohne** die Seite.
 *
 * Der Aufrufer setzt zurück und nicht diese Stelle: `onReset` läuft über
 * denselben Weg wie jede andere Änderung an einem Filter, damit die Adresse
 * mitgeht.
 */
export function FilterReset<T extends object>({
  chosen,
  start,
  onReset,
}: {
  chosen: T;
  start: T;
  onReset: () => void;
}): ReactElement | null {
  if (!differs(chosen, start)) {
    return null;
  }

  return (
    <Button variant="outline" size="md" icon="rotate-ccw" onClick={onReset}>
      {RESET}
    </Button>
  );
}

/**
 * Weicht der Ausschnitt von der Vorbelegung ab? Feld für Feld — alle drei
 * Vorbelegungen tragen ausschließlich flache Werte, und über die Felder der
 * Vorbelegung gelaufen, damit ein neuer Filter hier nicht stillschweigend
 * durchfällt.
 */
function differs<T extends object>(chosen: T, start: T): boolean {
  return (Object.keys(start) as (keyof T)[]).some(
    (field) => !Object.is(chosen[field], start[field]),
  );
}

/** „1–25 von 57" — die Schreibweise des Design-Systems für einen Seitenbereich. */
export function rangeOf(page: number, perPage: number, total: number): string {
  const first = (page - 1) * perPage + 1;

  return `${formatNumber(first)}–${formatNumber(Math.min(page * perPage, total))} von ${formatNumber(total)}`;
}

/**
 * Die Fläche, auf der eine Ansicht ihre Listen stapelt: die volle Breite des
 * Fensters, nicht eine feste Spalte.
 *
 * Feste Breiten standen hier, solange eine Ansicht aus Karten bestand — eine
 * Karte über 1900 Pixel liest sich schlecht. Eine Liste ist das Gegenteil: Jeder
 * Pixel, den sie nicht bekommt, wird eine Spalte, die aus dem Bild scrollt.
 *
 * **Bis an die Kante, gegen den Seitenrand gerechnet.** Die Inhaltsfläche der
 * Hülle hält einen Rand von `--sp-10`; er ist für Karten und Formulare richtig
 * und für eine Liste falsch — die Zeilenfläche endete sonst sichtbar vor dem
 * Fensterrand. Der Rand wird deshalb hier zurückgenommen, mit demselben Wert,
 * den die Hülle setzt. Das ist die Schreibweise des Design-Systems für einen
 * Listenabschnitt: „Record sections are full-bleed with negative 24px margins,
 * divided by a single top rule, never boxed."
 */
export function ListPage({ children }: { children: ReactNode }): ReactElement {
  return (
    <div
      style={{
        width: "calc(100% + 2 * var(--sp-10))",
        margin: "0 calc(-1 * var(--sp-10))",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {children}
    </div>
  );
}
